import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Lightweight i18n (no external dep). Marketing copy stays original; UI chrome localised.
const DICT = {
    en: {
        nav: {
            cabins: "Cabins",
            experiences: "Experiences",
            story: "Our Story",
            gallery: "Gallery",
            contact: "Contact",
            signIn: "Sign In",
            reserve: "Reserve Stay",
            myReservations: "My Reservations",
            adminDashboard: "Admin Dashboard",
            profile: "Profile & loyalty",
            signOut: "Sign out",
        },
        common: {
            loading: "Loading…",
            cancel: "Cancel",
            from: "From",
            perNight: "/ night",
            maxGuests: "Max guests",
            minStay: "Min stay",
            nights: "nights",
            night: "night",
            breakfast: "Breakfast",
            included: "Included",
            adultsOnly: "Adults Only · Negril, Jamaica",
            language: "Language",
        },
        hero: {
            title1: "Kotch pon a Rock",
            title2_em: "in Negril,",
            title2: "Jamaica.",
            titleGreen: "Kotch pon",
            titleGold: "a Rock in",
            titleRed: "Negril, Jamaica.",
            sub: "Wild Coffee Retreat is a hidden gem—seven treehouse-style cabins tucked behind rustic stone walls and hugged by lush gardens filled with the sounds of chirping birds, hummingbirds defending their territory, and darting through the trees. An adults-only retreat, this peaceful escape comfortably sleeps up to 18 guests and is a three-time Traveler's Choice winner.",
            ctaBook: "Reserve a Stay",
            ctaExplore: "Explore the Cabins",
        },
        cabinsPage: {
            eyebrow: "The Cabins",
            title1: "Everyone's going to love this",
            title2: "Each a bold work of its own.",
            sub: "Every treehouse-style cabin is hand-built, staggeringly designed, and air-conditioned with a private bath, refrigerator, veranda and high-speed Wi-Fi. Choose the one that calls to you — or let us match you to the right perch for your stay.",
            viewCabin: "View cabin",
            ourRoomsTitle: "Our rooms",
            ourRoomsBody: "Each of our thoughtfully designed treehouse cabins features air conditioning, ceiling fans, private bathrooms with hot water, refrigerators, and inviting verandas. High-speed internet is available throughout the property, along with a unique plunge pool offering both seating and swinging areas.",
            stayInfoTitle: "Adults Only",
            stayCheckIn: "Check-in 3:00 PM",
            stayCheckOut: "Check-out 11:00 AM",
        },
        cabinDetail: {
            cabin: "Cabin",
            inTheCabin: "In the cabin",
            gallery: "Gallery",
            guestReviews: "Guest reviews",
            saying: "What guests are saying.",
            empty: "No reviews yet — be the first to share your stay.",
            mustComplete: "Reviews are open to guests who have completed a stay with us.",
            reserveCabin: "Reserve this cabin",
            confirmNote: "No payment now. We confirm availability within 24 hours.",
            viewGallery: "View gallery",
            weekendPremiumBadge: (pct) => `+${pct}% on weekends`,
            hasSeasons: "Seasonal pricing",
            reviewForm: {
                eyebrow: "Leave a review",
                title: "Share your stay.",
                rating: "Your rating",
                titleField: "Title (optional)",
                bodyField: "Your thoughts *",
                submit: "Submit review",
                moderated: "Reviews are moderated before publication.",
                signInPrompt: "Sign in to share your impressions of this cabin.",
                signIn: "Sign in",
                stayedHere: "Stayed here?",
            },
        },
        book: {
            eyebrow: "Reserve",
            title: "Plan your stay.",
            step1: "01 — Choose a cabin",
            step2: "02 — Dates & guests",
            step3: "03 — Add experiences (optional)",
            step4: "04 — Guest information",
            dates: "Stay dates",
            checkin: "Check in",
            checkout: "Check out",
            pickDate: "Pick a date",
            guests: "Guests",
            guestN: (n) => `${n} guest${n === 1 ? "" : "s"}`,
            fullName: "Full name *",
            email: "Email *",
            phone: "Phone",
            specialRequests: "Special requests",
            summary: "Reservation summary",
            selectCabin: "Select a cabin",
            lodging: "Lodging",
            estimatedTotal: "Estimated total",
            request: "Request reservation",
            sending: "Sending…",
            noPaymentNote: "No payment required. We'll confirm availability and pricing within 24 hours.",
            availabilityChecking: "Checking availability…",
            availabilityAll: "All nights available",
            availabilitySome: (n) => `${n} night${n === 1 ? "" : "s"} already booked`,
            warningOverlap: "Selected range contains nights that are already booked. Please pick different dates.",
            rateBreakdown: "Per-night pricing",
            weekendPremium: "Weekend premium",
            seasonRate: "Season rate",
            standardRate: "Standard rate",
            step5: "05 — Payment",
            choosePayment: "How would you like to pay?",
            payCard: "Credit / Debit Card",
            payPaypal: "PayPal",
            payPowertranz: "Card (3-D Secure)",
            payBank: "Bank transfer",
            bank: {
                title: "Pay by bank transfer",
                subtitle: (amt) => `Transfer ${amt} to confirm your reservation.`,
                amount: "Amount",
                reference: "Reference",
                bankName: "Bank",
                accountName: "Account name",
                accountNumber: "Account number / IBAN",
                routing: "Routing / Sort code",
                swift: "SWIFT / BIC",
                done: "Done",
            },
            reserveLater: "Reserve now, pay later",
            reserveLaterNote: "Hold your dates — our team will follow up to collect payment.",
            payNow: (amt) => `Pay ${amt} & reserve`,
            paying: "Processing payment…",
            securePayment: "Secure payment",
            cardEntry: "Enter your card details",
            cardName: "Cardholder name",
            cardNumber: "Card number",
            cardExpM: "Exp. month",
            cardExpY: "Exp. year",
            cardCvv: "CVV",
            cardIncomplete: "Please complete all card fields.",
            paymentSuccess: "Payment received — your stay is confirmed!",
            paymentCancelled: "Payment was cancelled. Your dates are still held as pending.",
            paymentFailed: "We couldn't complete the payment. Please try again.",
            redirectingPaypal: "Redirecting you to PayPal…",
        },
        auth: {
            welcomeBack: "Welcome back",
            signInTitle: "Sign in",
            beginStay: "Begin your stay",
            createTitle: "Create account",
            yourName: "Your name",
            email: "Email",
            password: "Password",
            passwordMin: "Password (min 6)",
            signIn: "Sign in",
            signingIn: "Signing in…",
            createAccount: "Create account",
            creating: "Creating…",
            or: "Or",
            continueGoogle: "Continue with Google",
            newToRetreat: "New to the retreat?",
            createOne: "Create an account",
            alreadyGuest: "Already a guest?",
        },
        dashboard: {
            eyebrow: "Your reservations",
            welcome: (name) => `Welcome back, ${name}.`,
            newBooking: "New reservation",
            empty: {
                eyebrow: "No reservations yet",
                title: "Begin your first stay",
                sub: "Pick a cabin and dates. We'll handle the rest.",
                browse: "Browse cabins",
            },
            reservation: "Reservation",
            dates: "Dates",
            total: "Total",
            addons: "Add-ons",
            ref: "Ref",
            exportIcs: "Add to calendar",
            icsFailed: "Could not download calendar file.",
            payBalance: "Pay now",
            payBalanceTitle: "Complete your payment",
            payChooseMethod: "Choose how to pay",
            payContinue: "Continue",
            paySuccess: "Payment received — your stay is confirmed.",
        },
        profile: {
            eyebrow: "Profile & loyalty",
            title: "Your account.",
            personal: "Personal details",
            name: "Full name",
            email: "Email",
            phone: "Phone",
            picture: "Profile photo",
            uploadPhoto: "Upload photo",
            uploading: "Uploading…",
            notAnImage: "That file isn't an image",
            save: "Save changes",
            saving: "Saving…",
            loyalty: "Loyalty",
            currentTier: "Current tier",
            stats: "Your stays",
            totalNights: "Total nights",
            totalStays: "Completed stays",
            totalSpent: "Total spent",
            perks: "Your perks",
            nextTier: "Next tier",
            nightsToNext: (n, name) => `${n} more night${n === 1 ? "" : "s"} to reach ${name}`,
            allTiers: "All tiers",
            current: "Current",
        },
        gallery: {
            eyebrow: "Gallery",
            title1: "A subtle atmosphere,",
            title2: "inviting you to slow down",
            empty: "No photos in this category yet.",
            cat: {
                all: "All",
                cabins: "Cabins",
                art: "Art & murals",
                grounds: "Grounds & gardens",
                eatery: "Eatery & bar",
                experiences: "Experiences",
            },
        },
        newsletter: {
            heading: "Stay in touch.",
            sub: "Cabin openings, plunge-pool days and quiet Negril news — once a month, no more.",
            emailPlaceholder: "Your email",
            namePlaceholder: "Your name (optional)",
            subscribe: "Subscribe",
            subscribing: "Subscribing…",
            success: "Thank you — you're on the list.",
            error: "Could not subscribe. Try again.",
        },
        footer: {
            eyebrowBoutique: "A boutique adults-only retreat in Negril, Jamaica",
            line1: "Hummingbirds at dawn.",
            line2: "Plunge pool at noon.",
            line3_em: "Stars between the trees.",
            visit: "Visit",
            findUs: "Find us",
            adultsOnly: "Adults only · Home of Artical people",
            rights: "All rights reserved.",
        },
        contact: {
            eyebrow: "Contact",
            title1: "Send a note.",
            title2_em: "We'll write back.",
            yourName: "Your name",
            email: "Email",
            phoneOpt: "Phone (optional)",
            subject: "Subject",
            message: "Your message *",
            send: "Send message",
            sending: "Sending…",
            reservations: "Reservations",
            press: "Press & partnerships",
            visit: "Visit by appointment",
            success: "Thank you. We'll reply within 24 hours.",
            error: "Something went wrong. Please try again.",
        },
        status: {
            pending: "Pending",
            confirmed: "Confirmed",
            cancelled: "Cancelled",
            completed: "Completed",
        },
        toast: {
            welcomeBack: (name) => `Welcome back, ${name}.`,
            welcomeNew: (name) => `Welcome, ${name}.`,
            signInFailed: "Sign in failed",
            registerFailed: "Registration failed",
            passwordMin: "Password must be at least 6 characters.",
            saved: "Saved.",
            saveFailed: "Save failed",
            bookingSent: "Reservation request received. We'll confirm within 24 hours.",
            bookingFailed: "Booking failed",
            pickDatesError: "Please pick a cabin and dates.",
            checkoutAfterError: "Check-out must be after check-in.",
            datesUnavailable: "Selected dates include unavailable nights for this cabin.",
            inquirySent: "Thank you. We'll reply within 24 hours.",
            inquiryError: "Something went wrong. Please try again.",
            reviewSubmitted: "Thank you — your review will appear once approved.",
            reviewError: "Could not submit review",
            ratingNeeded: "Please choose a rating.",
            reviewBodyShort: "Please write at least a few words.",
            updated: "Updated",
            updateFailed: "Update failed",
            deleted: "Deleted",
            deleteFailed: "Delete failed",
            removed: "Removed",
            markedAs: (s) => `Marked ${s}`,
        },
        story: {
            eyebrow: "Our Story",
            title1: "Kotch pon a Rock —",
            title2_em: "a hidden gem",
            title2: "in Negril, Jamaica.",
            ch1: "Chapter 01",
            ch1_body: "Wild Coffee Retreat is a hidden gem: “Kotch pon a Rock in Negril, Jamaica.” A secluded escape, seven treehouse-style cabins, with their staggeringly bold design, pique your curiosity about what the artist was thinking while creating this space. Tucked behind rustic stone walls and hugged by lush gardens, the area comes alive with the sounds of hummingbirds chirping and darting through the trees.",
            ch2: "Chapter 02",
            ch2_body: "Voted a Traveler’s Choice Award winner for three consecutive years, the retreat offers a peaceful escape that “everyone is gonna love it.” It is perfect for singles, couples, or medium-sized groups of up to sixteen guests. We exclusively cater to adults, ensuring a tranquil and intimate atmosphere.",
            ch3: "Chapter 03",
            ch3_body: "Our thoughtfully designed cabins feature air conditioning, ceiling fans, private bathrooms with hot and cold water, refrigerators, inviting verandas, and high-speed internet accessible throughout the property. A tucked-away plunge pool provides both seating and swinging areas, adding to the charm of the space. Guests can also enjoy our on-site eatery and bar, where warm hospitality meets bold artistic design.",
            closing: "Wild Coffee Retreat is more than just a getaway; it is an experience of peace, creativity, and authenticity.",
            tagline: "“Home of Artical people.”",
        },
        loyaltyDiscount: {
            line: (tier, pct) => `${tier} member discount (-${pct}%)`,
            applied: (tier) => `Your ${tier} tier perk applied — enjoy the stay.`,
        },
        admin: {
            overview: "Overview",
            analytics: "Analytics",
            reservations: "Reservations",
            cabins: "Cabins",
            experiences: "Experiences",
            inquiries: "Inquiries",
            reviews: "Reviews",
            newsletter: "Newsletter",
            guests: "Guests",
            accounting: "Accounting",
            integrations: "APIs",
            settings: "Settings",
            employees: "Employees",
            gallery: "Gallery",
            galleryTitle: "The gallery.",
            gallerySubtitle: "Upload and organise photos of the property by category. These appear on the public gallery page.",
            galleryCategory: "Category",
            galleryCaption: "Caption",
            galleryCaptionPlaceholder: "Optional caption",
            galleryUpload: "Upload images",
            galleryUploading: "Uploading…",
            galleryUploadVideo: "Upload video",
            galleryUploadingVideo: "Uploading video…",
            galleryVideoHint: "Videos: MP4 · WEBM · up to 50 MB each.",
            galleryVideoUploaded: "Video uploaded",
            galleryHint: "Select images from your device. PNG · JPG · WEBP · up to 8 MB each.",
            galleryUploaded: "Images uploaded",
            galleryRemoved: "Image removed",
            galleryEmpty: "No images yet. Upload your first photo above.",
            galleryReorderHint: "Drag images to reorder. Star an image to feature it on the homepage.",
            galleryFeature: "Feature on homepage",
            galleryUnfeature: "Remove from homepage",
            noAccessTitle: "No sections assigned",
            noAccessBody: "Your account doesn't have access to any admin areas yet. Ask an administrator to grant you access.",
            signOut: "Sign out",
            atAGlance: "At a glance.",
            manage: "Manage all stays.",
            collection: "The collection.",
            curated: "Curated rituals.",
            messages: "Messages from guests.",
            moderate: "Moderate guest reviews.",
            subscribers: "Subscribers.",
            book: "Everyone in the book.",
            insights: "Insights at a glance.",
            save: "Save",
            cancel: "Cancel",
            edit: "Edit",
            delete: "Delete",
            approve: "Approve",
            reject: "Reject",
            cabinDialogTitle: (n) => (n ? "Edit cabin" : "New cabin"),
            cabinDialogDesc: "Adjust the cabin profile, pricing, amenities and gallery. Changes appear on the marketing site after save.",
            experienceDialogTitle: (n) => (n ? "Edit experience" : "New experience"),
            experienceDialogDesc: "Edit the experience details, duration and pricing. Active experiences appear on the marketing site and can be added to bookings.",
            new: "New",
            export: "Export CSV",
            analyticsLabels: {
                revenue6m: "Revenue · last 6 months",
                revenueAllTime: "Revenue (all time)",
                averageBooking: "Average booking",
                totalBookings: "Total bookings",
                bookingsByStatus: "Bookings by status",
                topCabins: "Top cabins by revenue",
                guestsByTier: "Guests by loyalty tier",
                statusLabels: {
                    pending: "Pending",
                    confirmed: "Confirmed",
                    cancelled: "Cancelled",
                    completed: "Completed",
                    unknown: "Unknown",
                },
            },
            pricing: {
                title: "Dynamic pricing suggestions",
                eyebrow: "Next 30 days",
                noChange: "Hold price",
                applyAll: "Apply all suggestions",
                apply: "Apply",
                applied: "Applied — price updated.",
                reasonVeryHigh: "Demand is very high — raise the nightly rate",
                reasonHigh: "Strong demand — nudge the rate up",
                reasonBalanced: "Healthy balance — hold the current rate",
                reasonLow: "Soft demand — offer a small discount",
                reasonVeryLow: "Few bookings — run a promotional rate",
                cols: {
                    cabin: "Cabin",
                    occupancy: "Occupancy · 30d",
                    current: "Current",
                    suggested: "Suggested",
                    delta: "Δ",
                    reason: "Reason",
                },
                nightsBooked: (n) => `${n} / 30 nights booked`,
            },
            stats: {
                totalReservations: "Total reservations",
                pending: "Pending",
                confirmed: "Confirmed",
                cabins: "Cabins",
                guests: "Guests",
                newInquiries: "New inquiries",
                pendingReviews: "Pending reviews",
                newsletterSubs: "Newsletter subs",
                revenueConfirmed: "Revenue (confirmed)",
                occupancy30d: "Occupancy · 30d",
            },
        },
        cancel: {
            title: "Cancel reservation",
            chooseReason: "Are you sure you want to cancel this stay?",
            policy: "Cancellation policy",
            daysUntil: (n) => `${n} day${n === 1 ? "" : "s"} until check-in`,
            refundLabel: "Refund",
            keep: "Keep reservation",
            confirm: "Cancel reservation",
            cancelling: "Cancelling…",
            success: (amt) => `Reservation cancelled. ${amt} will be refunded.`,
            error: "Could not cancel. Please contact us.",
            cannotCancel: "This booking can no longer be cancelled.",
            cancelled: "Cancelled",
            policyTiers: {
                "Free cancellation": "Free cancellation",
                "75% refund": "75% refund",
                "50% refund": "50% refund",
                "No refund": "No refund",
            },
            policyLabels: {
                free_cancellation: "Free cancellation",
                refund_75: "75% refund",
                refund_50: "50% refund",
                no_refund: "No refund",
            },
            sameWeek: "Same week",
            daysBefore: (n) => `≥ ${n} days`,
        },
        home: {
            momentsEyebrow: "From the property",
            momentsTitle: "Celebrate Slow Living",
            momentsCta: "See full gallery",
        },
        employees: {
            title: "Team & access.",
            subtitle: "Add employees and choose exactly which admin areas each one can use. Settings and Employees stay owner-only.",
            add: "Add employee",
            addTitle: "Add employee",
            editTitle: "Edit employee",
            dialogDesc: "Set login details and tick the areas this person can access.",
            save: "Save employee",
            saved: "Employee saved",
            removed: "Employee removed",
            edit: "Edit",
            delete: "Remove",
            empty: "No employees yet.",
            deleteConfirm: (n) => `Remove ${n}? They will lose access immediately.`,
            nameEmailRequired: "Name and email are required",
            passwordMin: "Password must be at least 6 characters",
            password: "Password",
            resetPassword: "Reset password",
            leaveBlank: "Leave blank to keep current",
            jobTitle: "Job title",
            phone: "Phone",
            applyPreset: "Quick role preset",
            accessAreas: "Access areas",
            accessNote: "Employees only see the areas you tick. Settings and Employees are never grantable.",
            activityTitle: "Activity log",
            activityEmpty: "No activity yet.",
            ownersTitle: "Owners & admins",
            ownersEmpty: "No admins found.",
            ownerBadge: "Full access",
            ownersNote: "Owner/admin accounts have full access and are managed separately from employees and guests.",
            activeAccount: "Account active (can sign in)",
            active: "Active",
            disabled: "Disabled",
            noAccess: "No access",
            cols: { name: "Name", email: "Email", access: "Access", status: "Status" },
            presets: { manager: "Manager", front_desk: "Front desk", housekeeping: "Housekeeping", content: "Content" },
        },

        experiencesPage: {
            eyebrow: "Experiences",
            title1: "Let's get together",
            title2: "and feel alright",
            sub: "Add a sunset sail, a beachside spa ritual, or a private dinner at our on-site eatery to your stay. Each is hosted by a local guide, chef or artist.",
        },
        experienceContent: {
            "Single-Origin Coffee Cupping": {
                category: "Coffee",
                description: "A guided cupping of seven Jamaican micro-lots — Blue Mountain, Strawberry Hill and beyond — led by our resident barista in the on-site bar.",
            },
            "Forest Floor Spa Ritual": {
                category: "Wellness",
                description: "A 110-minute restorative ritual in the garden cabana using cold-brew coffee scrub, sea-grape clay and warm river stones.",
            },
            "Studio Visit: Ceramics & Pigments": {
                category: "Art",
                description: "A private session in our resident artist's studio crafting wheel-thrown vessels with foraged Jamaican pigments.",
            },
            "Sunrise Cordillera Trek": {
                category: "Nature",
                description: "A guided pre-dawn walk to a hidden overlook above Negril Cliffs, with breakfast served on the bluff as the sun rises.",
            },
        },
    },
    es: {
        nav: {
            cabins: "Cabañas",
            experiences: "Experiencias",
            story: "Nuestra Historia",
            gallery: "Galería",
            contact: "Contacto",
            signIn: "Iniciar Sesión",
            reserve: "Reservar Estadía",
            myReservations: "Mis Reservas",
            adminDashboard: "Panel de Administración",
            profile: "Perfil y lealtad",
            signOut: "Cerrar sesión",
        },
        common: {
            loading: "Cargando…",
            cancel: "Cancelar",
            from: "Desde",
            perNight: "/ noche",
            maxGuests: "Huéspedes máx.",
            minStay: "Estadía mínima",
            nights: "noches",
            night: "noche",
            breakfast: "Desayuno",
            included: "Incluido",
            adultsOnly: "Solo adultos · Negril, Jamaica",
            language: "Idioma",
        },
        hero: {
            title1: "Kotch pon a Rock",
            title2_em: "en Negril,",
            title2: "Jamaica.",
            titleGreen: "Kotch pon",
            titleGold: "a Rock en",
            titleRed: "Negril, Jamaica.",
            sub: "Wild Coffee Retreat es una joya escondida — siete cabañas-arte estilo treehouse, ocultas tras muros de piedra rústica y abrazadas por jardines frondosos donde los colibríes vuelan entre los árboles. Solo adultos. Hasta dieciocho huéspedes. Ganador del Traveler's Choice tres años seguidos.",
            ctaBook: "Reservar una Estadía",
            ctaExplore: "Conocer las Cabañas",
        },
        cabinsPage: {
            eyebrow: "Las Cabañas",
            title1: "Siete cabañas de arte.",
            title2: "Cada una una obra audaz.",
            sub: "Cada cabaña estilo treehouse es hecha a mano, con diseño audaz, aire acondicionado, baño privado, refrigerador, veranda y Wi-Fi de alta velocidad. Elige la que te llame — o déjanos guiarte hacia el lugar perfecto para tu estadía.",
            viewCabin: "Ver cabaña",
            ourRoomsTitle: "Nuestras habitaciones",
            ourRoomsBody: "Cada una de nuestras cabañas estilo treehouse, cuidadosamente diseñadas, ofrece aire acondicionado, ventiladores de techo, baños privados con agua caliente, refrigerador y verandas acogedoras. Internet de alta velocidad disponible en toda la propiedad, junto con una piscina única que ofrece áreas para sentarse y mecerse.",
            stayInfoTitle: "Solo adultos",
            stayCheckIn: "Llegada 3:00 PM",
            stayCheckOut: "Salida 11:00 AM",
        },
        cabinDetail: {
            cabin: "Cabaña",
            inTheCabin: "En la cabaña",
            gallery: "Galería",
            guestReviews: "Reseñas de huéspedes",
            saying: "Lo que dicen nuestros huéspedes.",
            empty: "Aún no hay reseñas — sé el primero en compartir tu estadía.",
            mustComplete: "Las reseñas están abiertas a quienes han completado una estadía con nosotros.",
            reserveCabin: "Reservar esta cabaña",
            confirmNote: "Sin pago ahora. Confirmamos la disponibilidad en 24 horas.",
            viewGallery: "Ver galería",
            weekendPremiumBadge: (pct) => `+${pct}% en fines de semana`,
            hasSeasons: "Precios por temporada",
            reviewForm: {
                eyebrow: "Deja una reseña",
                title: "Comparte tu estadía.",
                rating: "Tu calificación",
                titleField: "Título (opcional)",
                bodyField: "Tus impresiones *",
                submit: "Enviar reseña",
                moderated: "Las reseñas se moderan antes de publicarse.",
                signInPrompt: "Inicia sesión para compartir tus impresiones de esta cabaña.",
                signIn: "Inicia sesión",
                stayedHere: "¿Te alojaste aquí?",
            },
        },
        book: {
            eyebrow: "Reservar",
            title: "Planifica tu estadía.",
            step1: "01 — Elige una cabaña",
            step2: "02 — Fechas y huéspedes",
            step3: "03 — Añade experiencias (opcional)",
            step4: "04 — Información del huésped",
            dates: "Fechas de la estadía",
            checkin: "Llegada",
            checkout: "Salida",
            pickDate: "Elige una fecha",
            guests: "Huéspedes",
            guestN: (n) => `${n} huésped${n === 1 ? "" : "es"}`,
            fullName: "Nombre completo *",
            email: "Correo *",
            phone: "Teléfono",
            specialRequests: "Solicitudes especiales",
            summary: "Resumen de la reserva",
            selectCabin: "Selecciona una cabaña",
            lodging: "Alojamiento",
            estimatedTotal: "Total estimado",
            request: "Solicitar reserva",
            sending: "Enviando…",
            noPaymentNote: "No se requiere pago. Confirmaremos disponibilidad y precio en 24 horas.",
            availabilityChecking: "Verificando disponibilidad…",
            availabilityAll: "Todas las noches disponibles",
            availabilitySome: (n) => `${n} noche${n === 1 ? "" : "s"} ya reservada${n === 1 ? "" : "s"}`,
            warningOverlap: "El rango seleccionado contiene noches que ya están reservadas. Elige otras fechas.",
            rateBreakdown: "Tarifa por noche",
            weekendPremium: "Recargo fin de semana",
            seasonRate: "Tarifa de temporada",
            standardRate: "Tarifa estándar",
            step5: "05 — Pago",
            choosePayment: "¿Cómo te gustaría pagar?",
            payCard: "Tarjeta de crédito / débito",
            payPaypal: "PayPal",
            payPowertranz: "Tarjeta (3-D Secure)",
            payBank: "Transferencia bancaria",
            bank: {
                title: "Pagar por transferencia",
                subtitle: (amt) => `Transfiere ${amt} para confirmar tu reserva.`,
                amount: "Importe",
                reference: "Referencia",
                bankName: "Banco",
                accountName: "Titular de la cuenta",
                accountNumber: "Número de cuenta / IBAN",
                routing: "Código de ruta",
                swift: "SWIFT / BIC",
                done: "Listo",
            },
            reserveLater: "Reserva ahora, paga después",
            reserveLaterNote: "Aparta tus fechas — nuestro equipo te contactará para cobrar el pago.",
            payNow: (amt) => `Pagar ${amt} y reservar`,
            paying: "Procesando el pago…",
            securePayment: "Pago seguro",
            cardEntry: "Ingresa los datos de tu tarjeta",
            cardName: "Nombre del titular",
            cardNumber: "Número de tarjeta",
            cardExpM: "Mes venc.",
            cardExpY: "Año venc.",
            cardCvv: "CVV",
            cardIncomplete: "Completa todos los campos de la tarjeta.",
            paymentSuccess: "¡Pago recibido — tu estadía está confirmada!",
            paymentCancelled: "El pago fue cancelado. Tus fechas siguen reservadas como pendientes.",
            paymentFailed: "No pudimos completar el pago. Inténtalo de nuevo.",
            redirectingPaypal: "Redirigiéndote a PayPal…",
        },
        auth: {
            welcomeBack: "Bienvenida de nuevo",
            signInTitle: "Iniciar sesión",
            beginStay: "Comienza tu estadía",
            createTitle: "Crear cuenta",
            yourName: "Tu nombre",
            email: "Correo",
            password: "Contraseña",
            passwordMin: "Contraseña (mín 6)",
            signIn: "Iniciar sesión",
            signingIn: "Iniciando…",
            createAccount: "Crear cuenta",
            creating: "Creando…",
            or: "O",
            continueGoogle: "Continuar con Google",
            newToRetreat: "¿Nuevo en el retiro?",
            createOne: "Crea una cuenta",
            alreadyGuest: "¿Ya eres huésped?",
        },
        dashboard: {
            eyebrow: "Tus reservas",
            welcome: (name) => `Bienvenida de nuevo, ${name}.`,
            newBooking: "Nueva reserva",
            empty: {
                eyebrow: "Aún no tienes reservas",
                title: "Comienza tu primera estadía",
                sub: "Elige una cabaña y fechas. Nosotros nos encargamos del resto.",
                browse: "Ver cabañas",
            },
            reservation: "Reserva",
            dates: "Fechas",
            total: "Total",
            addons: "Adicionales",
            ref: "Ref",
            exportIcs: "Añadir al calendario",
            icsFailed: "No se pudo descargar el archivo de calendario.",
            payBalance: "Pagar ahora",
            payBalanceTitle: "Completa tu pago",
            payChooseMethod: "Elige cómo pagar",
            payContinue: "Continuar",
            paySuccess: "Pago recibido — tu estadía está confirmada.",
        },
        profile: {
            eyebrow: "Perfil y lealtad",
            title: "Tu cuenta.",
            personal: "Datos personales",
            name: "Nombre completo",
            email: "Correo",
            phone: "Teléfono",
            picture: "Foto de perfil",
            uploadPhoto: "Subir foto",
            uploading: "Subiendo…",
            notAnImage: "Ese archivo no es una imagen",
            save: "Guardar cambios",
            saving: "Guardando…",
            loyalty: "Lealtad",
            currentTier: "Tu categoría",
            stats: "Tus estadías",
            totalNights: "Noches totales",
            totalStays: "Estadías completadas",
            totalSpent: "Total gastado",
            perks: "Tus beneficios",
            nextTier: "Próxima categoría",
            nightsToNext: (n, name) => `${n} noche${n === 1 ? "" : "s"} más para alcanzar ${name}`,
            allTiers: "Todas las categorías",
            current: "Actual",
        },
        gallery: {
            eyebrow: "Galería",
            title1: "Momentos tranquilos,",
            title2: "fotografiados con calma.",
            empty: "Aún no hay fotos en esta categoría.",
            cat: {
                all: "Todas",
                cabins: "Cabañas",
                art: "Arte y murales",
                grounds: "Jardines",
                eatery: "Comedor y bar",
                experiences: "Experiencias",
            },
        },
        newsletter: {
            heading: "Mantengamos el contacto.",
            sub: "Aperturas de cabañas, días de piscina y noticias tranquilas de Negril — una vez al mes, no más.",
            emailPlaceholder: "Tu correo",
            namePlaceholder: "Tu nombre (opcional)",
            subscribe: "Suscribirse",
            subscribing: "Suscribiendo…",
            success: "Gracias — estás en la lista.",
            error: "No fue posible suscribirte. Inténtalo de nuevo.",
        },
        footer: {
            eyebrowBoutique: "Un retiro boutique solo para adultos en Negril, Jamaica",
            line1: "Colibríes al amanecer.",
            line2: "Piscina al mediodía.",
            line3_em: "Estrellas entre los árboles.",
            visit: "Visita",
            findUs: "Encuéntranos",
            adultsOnly: "Solo adultos · Home of Artical people",
            rights: "Todos los derechos reservados.",
        },
        contact: {
            eyebrow: "Contacto",
            title1: "Envía una nota.",
            title2_em: "Te responderemos.",
            yourName: "Tu nombre",
            email: "Correo",
            phoneOpt: "Teléfono (opcional)",
            subject: "Asunto",
            message: "Tu mensaje *",
            send: "Enviar mensaje",
            sending: "Enviando…",
            reservations: "Reservas",
            press: "Prensa y alianzas",
            visit: "Visita con cita previa",
            success: "Gracias. Te responderemos en 24 horas.",
            error: "Algo salió mal. Inténtalo de nuevo.",
        },
        status: {
            pending: "Pendiente",
            confirmed: "Confirmada",
            cancelled: "Cancelada",
            completed: "Completada",
        },
        toast: {
            welcomeBack: (name) => `Bienvenida de nuevo, ${name}.`,
            welcomeNew: (name) => `Bienvenida, ${name}.`,
            signInFailed: "Error al iniciar sesión",
            registerFailed: "Registro fallido",
            passwordMin: "La contraseña debe tener al menos 6 caracteres.",
            saved: "Guardado.",
            saveFailed: "Error al guardar",
            bookingSent: "Solicitud de reserva recibida. Confirmaremos en 24 horas.",
            bookingFailed: "Reserva fallida",
            pickDatesError: "Por favor elige una cabaña y fechas.",
            checkoutAfterError: "La fecha de salida debe ser después de la llegada.",
            datesUnavailable: "Las fechas seleccionadas incluyen noches no disponibles para esta cabaña.",
            inquirySent: "Gracias. Te responderemos en 24 horas.",
            inquiryError: "Algo salió mal. Inténtalo de nuevo.",
            reviewSubmitted: "Gracias — tu reseña aparecerá una vez aprobada.",
            reviewError: "No se pudo enviar la reseña",
            ratingNeeded: "Por favor elige una calificación.",
            reviewBodyShort: "Escribe al menos unas palabras.",
            updated: "Actualizado",
            updateFailed: "Error al actualizar",
            deleted: "Eliminado",
            deleteFailed: "Error al eliminar",
            removed: "Eliminado",
            markedAs: (s) => `Marcado como ${s}`,
        },
        story: {
            eyebrow: "Nuestra Historia",
            title1: "Kotch pon a Rock —",
            title2_em: "una joya escondida",
            title2: "en Negril, Jamaica.",
            ch1: "Capítulo 01",
            ch1_body: "Wild Coffee Retreat es una joya escondida: “Kotch pon a Rock in Negril, Jamaica.” Un escape recóndito, siete cabañas estilo treehouse cuyo diseño audaz despierta tu curiosidad sobre lo que el artista pensaba al crear este espacio. Ocultas tras muros de piedra rústica y abrazadas por jardines frondosos, el lugar cobra vida con el canto de los colibríes que revolotean entre los árboles.",
            ch2: "Capítulo 02",
            ch2_body: "Galardonado con el Traveler’s Choice Award durante tres años consecutivos, el retiro ofrece un escape apacible que “everyone is gonna love it.” Es perfecto para solteros, parejas o grupos medianos de hasta dieciséis huéspedes. Atendemos exclusivamente a adultos, garantizando un ambiente íntimo y tranquilo.",
            ch3: "Capítulo 03",
            ch3_body: "Nuestras cabañas, pensadas con detalle, ofrecen aire acondicionado, ventiladores de techo, baños privados con agua caliente y fría, refrigeradores, verandas acogedoras e internet de alta velocidad en toda la propiedad. Una piscina escondida brinda áreas para sentarse y mecerse, sumando encanto al espacio. Los huéspedes también pueden disfrutar de nuestro restaurante y bar, donde la hospitalidad cálida se une al diseño artístico audaz.",
            closing: "Wild Coffee Retreat es más que una escapada; es una experiencia de paz, creatividad y autenticidad.",
            tagline: "“Home of Artical people.”",
        },
        loyaltyDiscount: {
            line: (tier, pct) => `Descuento de miembro ${tier} (-${pct}%)`,
            applied: (tier) => `Beneficio de tu nivel ${tier} aplicado — disfruta tu estadía.`,
        },
        admin: {
            overview: "Resumen",
            analytics: "Analítica",
            reservations: "Reservas",
            cabins: "Cabañas",
            experiences: "Experiencias",
            inquiries: "Mensajes",
            reviews: "Reseñas",
            newsletter: "Boletín",
            guests: "Huéspedes",
            accounting: "Contabilidad",
            integrations: "APIs",
            settings: "Ajustes",
            employees: "Empleados",
            gallery: "Galería",
            galleryTitle: "La galería.",
            gallerySubtitle: "Sube y organiza fotos de la propiedad por categoría. Aparecen en la página pública de galería.",
            galleryCategory: "Categoría",
            galleryCaption: "Descripción",
            galleryCaptionPlaceholder: "Descripción opcional",
            galleryUpload: "Subir imágenes",
            galleryUploading: "Subiendo…",
            galleryUploadVideo: "Subir video",
            galleryUploadingVideo: "Subiendo video…",
            galleryVideoHint: "Videos: MP4 · WEBM · hasta 50 MB cada uno.",
            galleryVideoUploaded: "Video subido",
            galleryHint: "Selecciona imágenes desde tu dispositivo. PNG · JPG · WEBP · hasta 8 MB cada una.",
            galleryUploaded: "Imágenes subidas",
            galleryRemoved: "Imagen eliminada",
            galleryEmpty: "Aún no hay imágenes. Sube tu primera foto arriba.",
            galleryReorderHint: "Arrastra las imágenes para reordenarlas. Marca con estrella para destacarla en la página de inicio.",
            galleryFeature: "Destacar en inicio",
            galleryUnfeature: "Quitar de inicio",
            noAccessTitle: "Sin secciones asignadas",
            noAccessBody: "Tu cuenta aún no tiene acceso a ninguna área de administración. Pide a un administrador que te conceda acceso.",
            signOut: "Cerrar sesión",
            atAGlance: "De un vistazo.",
            manage: "Gestiona todas las estadías.",
            collection: "La colección.",
            curated: "Rituales curados.",
            messages: "Mensajes de los huéspedes.",
            moderate: "Modera las reseñas.",
            subscribers: "Suscriptores.",
            book: "Todos en el libro.",
            insights: "Información de un vistazo.",
            save: "Guardar",
            cancel: "Cancelar",
            edit: "Editar",
            delete: "Eliminar",
            approve: "Aprobar",
            reject: "Rechazar",
            cabinDialogTitle: (n) => (n ? "Editar cabaña" : "Nueva cabaña"),
            cabinDialogDesc: "Ajusta el perfil, precio, comodidades y galería de la cabaña. Los cambios aparecen en el sitio público al guardar.",
            experienceDialogTitle: (n) => (n ? "Editar experiencia" : "Nueva experiencia"),
            experienceDialogDesc: "Edita los detalles, la duración y el precio. Las experiencias activas aparecen en el sitio y pueden añadirse a las reservas.",
            new: "Nuevo",
            export: "Exportar CSV",
            analyticsLabels: {
                revenue6m: "Ingresos · últimos 6 meses",
                revenueAllTime: "Ingresos (histórico)",
                averageBooking: "Reserva promedio",
                totalBookings: "Reservas totales",
                bookingsByStatus: "Reservas por estado",
                topCabins: "Cabañas con más ingresos",
                guestsByTier: "Huéspedes por nivel de fidelidad",
                statusLabels: {
                    pending: "Pendiente",
                    confirmed: "Confirmada",
                    cancelled: "Cancelada",
                    completed: "Completada",
                    unknown: "Desconocido",
                },
            },
            pricing: {
                title: "Sugerencias de precios dinámicas",
                eyebrow: "Próximos 30 días",
                noChange: "Mantener precio",
                applyAll: "Aplicar todas las sugerencias",
                apply: "Aplicar",
                applied: "Aplicado — precio actualizado.",
                reasonVeryHigh: "Demanda muy alta — sube la tarifa por noche",
                reasonHigh: "Demanda fuerte — sube la tarifa con suavidad",
                reasonBalanced: "Equilibrio sano — mantén la tarifa actual",
                reasonLow: "Demanda baja — aplica un pequeño descuento",
                reasonVeryLow: "Pocas reservas — lanza una tarifa promocional",
                cols: {
                    cabin: "Cabaña",
                    occupancy: "Ocupación · 30d",
                    current: "Actual",
                    suggested: "Sugerida",
                    delta: "Δ",
                    reason: "Motivo",
                },
                nightsBooked: (n) => `${n} / 30 noches reservadas`,
            },
            stats: {
                totalReservations: "Reservas totales",
                pending: "Pendientes",
                confirmed: "Confirmadas",
                cabins: "Cabañas",
                guests: "Huéspedes",
                newInquiries: "Nuevos mensajes",
                pendingReviews: "Reseñas pendientes",
                newsletterSubs: "Suscriptores",
                revenueConfirmed: "Ingresos (confirmados)",
                occupancy30d: "Ocupación · 30d",
            },
        },
        cancel: {
            title: "Cancelar reserva",
            chooseReason: "¿Seguro que quieres cancelar esta estadía?",
            policy: "Política de cancelación",
            daysUntil: (n) => `${n} día${n === 1 ? "" : "s"} hasta la llegada`,
            refundLabel: "Reembolso",
            keep: "Mantener reserva",
            confirm: "Cancelar reserva",
            cancelling: "Cancelando…",
            success: (amt) => `Reserva cancelada. Se reembolsarán ${amt}.`,
            error: "No fue posible cancelar. Contáctanos.",
            cannotCancel: "Esta reserva ya no se puede cancelar.",
            cancelled: "Cancelada",
            policyTiers: {
                "Free cancellation": "Cancelación gratuita",
                "75% refund": "75% de reembolso",
                "50% refund": "50% de reembolso",
                "No refund": "Sin reembolso",
            },
            policyLabels: {
                free_cancellation: "Cancelación gratuita",
                refund_75: "75% de reembolso",
                refund_50: "50% de reembolso",
                no_refund: "Sin reembolso",
            },
            sameWeek: "Esta semana",
            daysBefore: (n) => `≥ ${n} días`,
        },
        home: {
            momentsEyebrow: "Desde la propiedad",
            momentsTitle: "Momentos en Wild Coffee.",
            momentsCta: "Ver galería completa",
        },
        employees: {
            title: "Equipo y acceso.",
            subtitle: "Agrega empleados y elige exactamente qué áreas del panel puede usar cada uno. Ajustes y Empleados son solo para el propietario.",
            add: "Agregar empleado",
            addTitle: "Agregar empleado",
            editTitle: "Editar empleado",
            dialogDesc: "Configura los datos de acceso y marca las áreas que esta persona puede usar.",
            save: "Guardar empleado",
            saved: "Empleado guardado",
            removed: "Empleado eliminado",
            edit: "Editar",
            delete: "Eliminar",
            empty: "Aún no hay empleados.",
            deleteConfirm: (n) => `¿Eliminar a ${n}? Perderá el acceso de inmediato.`,
            nameEmailRequired: "El nombre y el correo son obligatorios",
            passwordMin: "La contraseña debe tener al menos 6 caracteres",
            password: "Contraseña",
            resetPassword: "Restablecer contraseña",
            leaveBlank: "Déjalo en blanco para mantener la actual",
            jobTitle: "Puesto",
            phone: "Teléfono",
            applyPreset: "Rol rápido",
            accessAreas: "Áreas de acceso",
            accessNote: "Los empleados solo ven las áreas que marques. Ajustes y Empleados nunca se pueden conceder.",
            activityTitle: "Registro de actividad",
            activityEmpty: "Sin actividad todavía.",
            ownersTitle: "Propietarios y administradores",
            ownersEmpty: "No se encontraron administradores.",
            ownerBadge: "Acceso total",
            ownersNote: "Las cuentas de propietario/administrador tienen acceso total y se gestionan por separado de empleados e invitados.",
            activeAccount: "Cuenta activa (puede iniciar sesión)",
            active: "Activo",
            disabled: "Desactivado",
            noAccess: "Sin acceso",
            cols: { name: "Nombre", email: "Correo", access: "Acceso", status: "Estado" },
            presets: { manager: "Gerente", front_desk: "Recepción", housekeeping: "Limpieza", content: "Contenido" },
        },

        experiencesPage: {
            eyebrow: "Experiencias",
            title1: "Rituales pausados,",
            title2: "con raíces en Negril.",
            sub: "Añade un paseo al atardecer, un ritual de spa junto a la playa o una cena privada en nuestro restaurante. Cada experiencia la guía un local: chef, artista o anfitrión.",
        },
        experienceContent: {
            "Single-Origin Coffee Cupping": {
                category: "Café",
                description: "Una cata guiada de siete micro-lotes jamaicanos — Blue Mountain, Strawberry Hill y más — dirigida por nuestro barista residente en el bar del retiro.",
            },
            "Forest Floor Spa Ritual": {
                category: "Bienestar",
                description: "Un ritual restaurador de 110 minutos en la cabaña del jardín con exfoliante de café frío, arcilla de uva marina y piedras tibias del río.",
            },
            "Studio Visit: Ceramics & Pigments": {
                category: "Arte",
                description: "Una sesión privada en el estudio de nuestra artista residente creando vasijas en torno con pigmentos jamaicanos recolectados localmente.",
            },
            "Sunrise Cordillera Trek": {
                category: "Naturaleza",
                description: "Una caminata guiada antes del amanecer hasta un mirador escondido sobre los Acantilados de Negril, con desayuno servido en el acantilado mientras sale el sol.",
            },
        },
    },
};

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        try { return localStorage.getItem("wcr_lang") || "en"; } catch { return "en"; }
    });
    const setLang = useCallback((l) => {
        setLangState(l);
        try { localStorage.setItem("wcr_lang", l); } catch (e) { console.debug("language preference not persisted", e); }
    }, []);
    useEffect(() => {
        document.documentElement.lang = lang;
    }, [lang]);

    const t = useCallback((path, ...args) => {
        const parts = path.split(".");
        let v = DICT[lang] || DICT.en;
        for (const p of parts) {
            if (v == null) break;
            v = v[p];
        }
        if (v == null) {
            v = DICT.en;
            for (const p of parts) {
                if (v == null) break;
                v = v[p];
            }
        }
        if (typeof v === "function") return v(...args);
        return v ?? path;
    }, [lang]);

    const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
    return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useT() {
    const ctx = useContext(LangContext);
    if (!ctx) throw new Error("useT outside LanguageProvider");
    return ctx;
}
